import { errorToast } from "@/components/ui/toast.tsx";
import { artifactQueries } from "@/hooks/artifactQueries.ts";
import type { GetArtifactResp } from "@/types/artifacts.ts";
import type { FieldPatch } from "@/types/artifacts-view.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { useCallback } from "react";
import { trackArtifactEdit } from "@/components/artifacts/view/pendingArtifactEdits.ts";

export type { FieldPatch };

interface UsePatchArtifactOptions {
  threadId: number;
  messageId: number;
  artifactId: string;
  onErrorMessage?: string;
}

/** The `{ error }` body a 4xx from the artifact endpoints carries, if present. */
function serverErrorMessage(error: unknown): string | undefined {
  const body = (error as { response?: { data?: unknown } })?.response?.data;
  if (body && typeof body === "object" && "error" in body) {
    const message = (body as { error?: unknown }).error;
    if (typeof message === "string" && message.trim().length > 0) return message;
  }
  return undefined;
}

/**
 * Generic mutation hook for the schema-driven artifact view. Posts
 *   { patches: [{ path, value }, ...] }
 * to /a/threads/:tid/messages/:mid/artifacts/:id/patch and reconciles every
 * cache an artifact renders from with the refreshed response. Per-block
 * components call `patchOne(path, value)` for single-field blur saves, or
 * `patch(...)` to batch a multi-field save (e.g. start + end date together)
 * atomically.
 *
 * The same artifact is cached under two independent keys, and a write has to
 * reach both or the user's edit is invisible on the surface they made it on:
 *   - `artifactQueries.detail` — the in-thread card and the omnibox biscuit,
 *     both via `useArtifact`.
 *   - `artifactQueries.byId` — the canvas modal, which addresses artifacts by
 *     id alone and so cannot use the thread-scoped key above.
 */
export const usePatchArtifact = ({
  threadId,
  messageId,
  artifactId,
  onErrorMessage = "Error updating artifact",
}: UsePatchArtifactOptions) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (patches: FieldPatch[]) => {
      const response = await api.post<GetArtifactResp>(
        `/api/a/threads/${threadId}/messages/${messageId}/artifacts/${artifactId}/patch`,
        { patches },
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        artifactQueries.detail(threadId, messageId, artifactId).queryKey,
        data,
      );
      // The canvas modal fetches the artifact by id alone, a different cache
      // entry than the detail key above. Without this write it re-renders the
      // view it first fetched, so an edit made in the canvas is invisible in
      // the canvas — the user re-edits, or approves a card showing the values
      // they thought they had replaced.
      queryClient.setQueryData(artifactQueries.byId(artifactId).queryKey, data);
    },
    onError: (error) => {
      console.error(error);
      // The server composes an actionable sentence for a rejected edit — "this
      // is a recurring meeting on MO, TU… pick a time on one of those days".
      // Swallowing it into a generic toast leaves the user with a card that
      // won't take their change and no idea why.
      errorToast(serverErrorMessage(error) ?? onErrorMessage);
    },
  });

  // `mutation.mutateAsync` is a stable reference per TanStack Query, so an
  // empty deps array is safe. Wrapping in useCallback keeps `patch` and
  // `patchOne` stable across renders so consumers (notably
  // `ArtifactViewContext`) can usefully memoize.
  // Tracked so an artifact action can wait for a blur-save that is still in
  // flight instead of racing it — see `flushPendingArtifactEdits`.
  const patch = useCallback(
    (patches: FieldPatch[]) => trackArtifactEdit(mutation.mutateAsync(patches)),
    [mutation.mutateAsync],
  );
  const patchOne = useCallback(
    (path: string, value: unknown) => trackArtifactEdit(mutation.mutateAsync([{ path, value }])),
    [mutation.mutateAsync],
  );

  return {
    patch,
    patchOne,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};
